/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from "react";
import { type TSlide } from "@/zapi-frontend/stores/presentation";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { SlideOutlineEditor } from "./SlideOutlineEditor";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

type TSlideCardProps = {
  slide: TSlide;
  index: number;
  onUpdate: (slideId: string, updates: Partial<TSlide>) => void;
  onDelete: (slideId: string) => void;
  selectedIndex: number;
  onSelect: (index: number) => void;
};

export function SlideCard(props: TSlideCardProps) {
  const { slide, onUpdate, index, selectedIndex, onSelect } = props;
  const { isLabs = false } = useMeStore();

  const [title, setTitle] = useState(slide.title || "");
  const [outline, setOutline] = useState(slide.outline || "");
  const titleRef = useRef<HTMLTextAreaElement>(null);

  const adjustTitleHeight = useCallback(() => {
    const textarea = titleRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, isLabs ? 120 : 150)}px`;
  }, [isLabs]);

  useEffect(() => {
    if (slide.title !== title) setTitle(slide.title || "");
    if (slide.outline !== outline) setOutline(slide.outline || "");
  }, [slide.title, slide.outline]);

  useEffect(() => {
    adjustTitleHeight();
  }, [title, adjustTitleHeight]);

  useEffect(() => {
    const textarea = titleRef.current;
    if (!textarea) return;

    const resizeObserver = new ResizeObserver(() => {
      adjustTitleHeight();
    });

    resizeObserver.observe(textarea.parentElement!);

    return () => resizeObserver.disconnect();
  }, [adjustTitleHeight]);

  function changeTitleHandler(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const { value } = e.target;
    setTitle(value);
    onUpdate(slide.id, { title: value });
  }

  const changeOutlineHandler = useCallback(
    (value: string) => {
      setOutline(value);
      onUpdate(slide.id, { outline: value });
    },
    [slide.id, onUpdate]
  );

  return (
    <div
      onClick={() => onSelect(index)}
      id={`slide-${slide.id}`}
      className={cn(
        "w-full border p-10 transition-all hover:border-innoveraOrange-400 flex flex-col gap-4 relative group",
        isLabs ? "h-[480px]" : "h-[800px]",
        "bg-white",
        selectedIndex === index && "border-innoveraOrange-400"
      )}
    >
      <textarea
        ref={titleRef}
        className={cn(
          "font-bold w-full border-none focus:outline-none focus:ring-2 focus:ring-innoveraOrange-400 focus:ring-offset-2 rounded-lg px-2 py-1 bg-transparent placeholder:text-innoveraZinc-400 text-innoveraZinc-900 transition-all resize-none overflow-hidden leading-tight",
          isLabs ? "text-3xl" : "text-4xl"
        )}
        value={title}
        onChange={changeTitleHandler}
        placeholder="Slide Title"
        rows={1}
      />

      <div className="flex-1 w-full overflow-hidden">
        <SlideOutlineEditor
          value={outline}
          onChange={changeOutlineHandler}
          placeholder="• Key point 1&#10;• Key point 2&#10;• Key point 3"
          isLabs={isLabs}
        />
      </div>
    </div>
  );
}
